import { Map, Set } from 'immutable'
import { createSelector } from 'reselect'
import { isNil, compose, map, filter, pick, equals } from 'ramda'
import { getIn, getInOr, get, valueSeq } from 'utils/immutable'
import { createToJsSelector, createDeepEqualSelector } from 'selectors/utils'

export const currentFeatureSelector = getIn(['feature', 'currentFeature'])

const getCurrentFeature = createToJsSelector(
  getIn(['feature', 'currentFeature'])
)
const getOriginalFeature = createToJsSelector(
  getIn(['feature', 'originalFeature'])
)

const getInitialFeature = createSelector(
  getCurrentFeature,
  (currentFeature) => currentFeature.feature.initial
)

const getIsPreCreateFeature = createSelector(
  getCurrentFeature,
  (currentFeature) => currentFeature.isPrecreate
)

const getIsFeatureInModalView = createSelector(
  getCurrentFeature,
  (currentFeature) => currentFeature.isModalView
)

const getIsSavingSelector = (state) =>
  state.getIn(['feature', 'isSaving'], false)

const getIsSaving = createSelector(getIsSavingSelector, (isSaving) => isSaving)

const featureDescriptionSelector = createSelector(
  getIn(['feature', 'currentFeature', 'feature']),
  (feature) => feature.valueSeq().first().get('description')
)

export const featureIdSelector = createDeepEqualSelector(
  getIn(['feature', 'currentFeature', 'feature']),
  (feature) => feature.valueSeq().first().get('id')
)

const importedEntitiesSelector = getInOr(Set(), [
  'feature',
  'currentFeature',
  'importedEntities',
])

const queriesSelector = getInOr(Map(), ['feature', 'currentFeature', 'queries'])

const outputsSelector = getInOr(Map(), ['feature', 'currentFeature', 'outputs'])

const functionsSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'functions',
])

const getValidationDescriptionValues = (importedEntitiesSet) =>
  compose(
    map(get('description')),
    filter((ent) => !importedEntitiesSet.has(ent.get('uri') || ent.get('id'))),
    valueSeq
  )

const validationDescriptionsSelector = createSelector(
  featureDescriptionSelector,
  queriesSelector,
  outputsSelector,
  functionsSelector,
  importedEntitiesSelector,
  (featureDescription, queries, outputs, functions, importedEntities) => {
    const getDescs = getValidationDescriptionValues(importedEntities)
    const queryDescriptions = getDescs(queries)
    const outputDescriptions = getDescs(outputs)
    const functionsDescriptions = getDescs(functions)

    return queryDescriptions
      .concat(outputDescriptions)
      .concat(functionsDescriptions)
      .concat(featureDescription)
  }
)

const getValidationDescriptions = createToJsSelector(
  validationDescriptionsSelector
)

const outputMapsSelector = getInOr(Map, [
  'feature',
  'currentFeature',
  'outputMaps',
])

const getIsOneOutputQueryDefined = createSelector(
  outputMapsSelector,
  (outputMaps) =>
    outputMaps.some((outputMap) => !isNil(outputMap.get('outputQuery')))
)

const isRequestingSignalsSelector = (state) =>
  state.getIn(['feature', 'isRequestingSignals'], true) // default is true as we need to request signals still

const getIsRequestingSignals = createSelector(
  isRequestingSignalsSelector,
  (isRequestingSignals) => isRequestingSignals
)

const getIsDuplicatingFeature = (state) =>
  state.getIn(['feature', 'isDuplicatingFeature'])

const selectFeatureIsDirty = (state) =>
  state.getIn(['feature', 'isDirty'], false)

const getFeatureIsDirty = createSelector(
  getOriginalFeature,
  getCurrentFeature,
  selectFeatureIsDirty,
  (original, current = {}, isDirty = false) => {
    if (isDirty === true) return true
    if (original === null) return false
    const props = [
      'conditions',
      'functions',
      'outputMaps',
      'outputs',
      'queries',
    ]
    const originalValues = pick(props, original)
    const currentValues = pick(props, current)
    const featuresEqual = Object.keys(originalValues)
      .map((key) => equals(originalValues[key], currentValues[key]))
      .reduce((result, value) => result && value, true)
    return !featuresEqual
  }
)

export {
  getIsDuplicatingFeature,
  getCurrentFeature,
  getOriginalFeature,
  getInitialFeature,
  getIsPreCreateFeature,
  getIsFeatureInModalView,
  getIsSaving,
  getValidationDescriptions,
  getIsOneOutputQueryDefined,
  getIsRequestingSignals,
  getFeatureIsDirty,
}
